import { http } from '@/lib/http';
import type { DocumentDto } from './platform';
import type { DateOnly, DateRangeParams, Decimal, PagedResult, RowVersion, UtcDateTime } from './types';

/**
 * Commercial (M2) — contracts, versions/amendments, orders + allocation, change requests, handover.
 * Mirrors apps/api/src/Crm.Api/Modules/Commercial/CommercialDtos.cs + CommercialRules.cs.
 * Decimals are strings EXCEPT inside `ChangeRequestDto.proposed/impact` (raw JSON documents serialized without the
 * decimal-string converter → numbers). Use `decimalOf()` from features/commercial/math.ts to read them.
 */

// ---------- Permissions (CommercialPerm) ----------
export const CP = {
  contractReadOwn: 'commercial.contract.read.own',
  contractReadAll: 'commercial.contract.read.all',
  contractWrite: 'commercial.contract.write',
  contractSubmit: 'commercial.contract.submit',
  contractSign: 'commercial.contract.sign',
  contractTerminate: 'commercial.contract.terminate',
  orderReadOwn: 'commercial.order.read.own',
  orderReadAll: 'commercial.order.read.all',
  orderWrite: 'commercial.order.write',
  orderSubmit: 'commercial.order.submit',
  orderCancel: 'commercial.order.cancel',
  changeRequestWrite: 'commercial.change_request.write',
  contractTypeManage: 'commercial.contract_type.manage',
  handoverProcess: 'operations.handover.process',
  orderProgress: 'operations.order.progress',
} as const;

export const CONTRACT_READ = [CP.contractReadOwn, CP.contractReadAll] as const;
export const ORDER_READ = [CP.orderReadOwn, CP.orderReadAll, CP.handoverProcess, CP.orderProgress] as const;
export const CHANGE_REQUEST_READ = [CP.contractReadOwn, CP.contractReadAll, CP.orderReadOwn, CP.orderReadAll] as const;

// ---------- Terms / snapshots ----------
export interface InstallmentPlanLine {
  seq: number;
  description: string;
  percent: Decimal | null;
  amount: Decimal | null;
  dueDate: DateOnly | null;
  dueCondition: string | null;
}

export interface ContractTerms {
  paymentTermsText: string | null;
  installmentPlan: InstallmentPlanLine[] | null;
  paymentAccount: string | null;
  servicePeriod: string | null;
  otherTerms: string | null;
  freeOfCharge: boolean;
}

export interface PartySnapshot {
  contactId: number;
  fullName: string;
  title: string | null;
  phone: string | null;
  email: string | null;
  legalBasis: string | null;
  effectiveFrom: DateOnly | null;
  effectiveTo: DateOnly | null;
  authorizationDocumentId: number | null;
}

export interface CustomerLegalSnapshot {
  customerId: number;
  customerCode: string;
  legalProfileId: number;
  legalProfileVersion: number;
  customerType: string;
  legalName: string;
  taxCode: string | null;
  registeredAddress: string | null;
  registeredProvinceCode: string | null;
  registeredProvinceName: string | null;
  legalRepresentative: PartySnapshot | null;
  authorizedSigner: PartySnapshot | null;
  primaryContact: PartySnapshot | null;
}

export interface ApprovalInstanceSummary {
  instanceId: number;
  requestTypeCode: string;
  status: string;
  currentLevelNo: number;
  levelCount: number;
  submittedAtUtc: UtcDateTime;
  completedAtUtc: UtcDateTime | null;
}

export interface ApprovalInstanceRef {
  instanceId: number;
  status: string;
  policyVersionId: number;
}

export interface CreatedResponse {
  id: number;
  code: string;
}

// ---------- Contract types ----------
export interface ContractTypeDto {
  id: number;
  code: string;
  name: string;
  requiresSignature: boolean;
  approvalPolicyHint: string | null;
  isActive: boolean;
  isDemo: boolean;
  version: RowVersion;
}

export interface ContractTypeRequest {
  /** Blank on create → series CONTRACT_TYPE (LHD001…); required on edit. */
  code: string;
  name: string;
  requiresSignature: boolean;
  approvalPolicyHint: string | null;
  isActive: boolean;
  version: RowVersion | null;
}

// ---------- Contracts ----------
export interface ContractItemInput {
  lineKey?: string | null;
  serviceId?: number | null;
  trainingCategoryId?: number | null;
  description: string;
  unit?: string | null;
  qty: Decimal;
  unitPrice: Decimal;
  discountPercent: Decimal;
  taxRate: Decimal;
}

export interface ContractItemDto {
  id: number;
  lineKey: string;
  sortOrder: number;
  serviceId: number | null;
  trainingCategoryId: number | null;
  description: string;
  unit: string;
  qty: Decimal;
  unitPrice: Decimal;
  discountPercent: Decimal;
  discountAmount: Decimal;
  taxRate: Decimal;
  taxAmount: Decimal;
  lineSubtotal: Decimal;
  netAmount: Decimal;
  grossAmount: Decimal;
}

export interface ContractVersionDto {
  id: number;
  versionNo: number;
  state: string;
  terms: ContractTerms;
  legalSnapshot: CustomerLegalSnapshot | null;
  signerRepresentativeId: number | null;
  contactId: number | null;
  effectiveFrom: DateOnly | null;
  effectiveTo: DateOnly | null;
  currency: string;
  subtotal: Decimal;
  discountTotal: Decimal;
  netTotal: Decimal;
  taxTotal: Decimal;
  grossTotal: Decimal;
  snapshotHash: string | null;
  lockedAtUtc: UtcDateTime | null;
  sourceQuoteVersionId: number | null;
  basedOnVersionId: number | null;
  changeRequestId: number | null;
  revisionReason: string | null;
  createdAtUtc: UtcDateTime;
  items: ContractItemDto[];
}

export interface ContractVersionSummaryDto {
  id: number;
  versionNo: number;
  state: string;
  grossTotal: Decimal;
  currency: string;
  snapshotHash: string | null;
  lockedAtUtc: UtcDateTime | null;
  changeRequestId: number | null;
  createdAtUtc: UtcDateTime;
}

export interface AmendmentDto {
  id: number;
  amendmentNo: number;
  baseVersionId: number;
  newVersionId: number;
  changeRequestId: number;
  reason: string;
  approvedAtUtc: UtcDateTime;
  appliedAtUtc: UtcDateTime;
  signedDocumentId: number | null;
  signedAtUtc: UtcDateTime | null;
}

export interface LineAllocationDto {
  contractItemId: number;
  lineKey: string;
  description: string;
  unit: string;
  qty: Decimal;
  gross: Decimal;
  allocatedQty: Decimal;
  allocatedAmount: Decimal;
  remainingQty: Decimal;
  remainingAmount: Decimal;
  thisOrderQty: Decimal | null;
  thisOrderAmount: Decimal | null;
}

export interface ContractOrderRow {
  id: number;
  orderCode: string;
  title: string;
  status: string;
  statusName: string;
  allocatedAmount: Decimal;
  currency: string;
  createdAtUtc: UtcDateTime;
}

export interface ContractListItem {
  id: number;
  contractCode: string;
  title: string;
  customerId: number;
  customerName: string;
  contractTypeCode: string;
  contractTypeName: string;
  status: string;
  statusName: string;
  salesOwnerUserId: number;
  salesOwnerName: string;
  grossTotal: Decimal;
  currency: string;
  signedAtUtc: UtcDateTime | null;
  effectiveFrom: DateOnly | null;
  effectiveTo: DateOnly | null;
  createdAtUtc: UtcDateTime;
}

export interface ContractDetail {
  id: number;
  contractCode: string;
  title: string;
  customerId: number;
  customerName: string;
  contractType: ContractTypeDto;
  status: string;
  statusName: string;
  salesOwnerUserId: number;
  salesOwnerName: string;
  currency: string;
  sourceQuotationId: number | null;
  currentVersionId: number | null;
  activeVersionId: number | null;
  submittedAtUtc: UtcDateTime | null;
  approvedAtUtc: UtcDateTime | null;
  signatureRequestedAtUtc: UtcDateTime | null;
  signedAtUtc: UtcDateTime | null;
  signerName: string | null;
  signedDocumentId: number | null;
  signedLegalSnapshot: CustomerLegalSnapshot | null;
  effectiveFrom: DateOnly | null;
  effectiveTo: DateOnly | null;
  statusReasonCode: string | null;
  statusReasonText: string | null;
  version: RowVersion;
  currentVersion: ContractVersionDto | null;
  activeVersion: ContractVersionDto | null;
  versions: ContractVersionSummaryDto[];
  amendments: AmendmentDto[];
  orders: ContractOrderRow[];
  allocation: LineAllocationDto[];
  approval: ApprovalInstanceSummary | null;
  documents: DocumentDto[];
  checklist: string[];
  openChangeRequestId: number | null;
  createdAtUtc: UtcDateTime;
}

export interface CreateContractRequest {
  customerId: number;
  contractTypeId: number;
  title?: string | null;
  currency?: string | null;
  effectiveFrom?: DateOnly | null;
  effectiveTo?: DateOnly | null;
  terms?: Partial<ContractTerms> | null;
  signerRepresentativeId?: number | null;
  contactId?: number | null;
  items?: ContractItemInput[] | null;
  salesOwnerUserId?: number | null;
  /** Optional manual code; null/blank → series CONTRACT. Immutable after creation. */
  code?: string | null;
}

export interface ContractFromQuotationRequest {
  quotationId: number;
  contractTypeId: number;
  effectiveFrom?: DateOnly | null;
  effectiveTo?: DateOnly | null;
  title?: string | null;
  signerRepresentativeId?: number | null;
  contactId?: number | null;
  /** Optional manual code; null/blank → series CONTRACT. */
  code?: string | null;
}

export interface UpdateContractDraftRequest {
  version: RowVersion;
  contractTypeId: number;
  title: string;
  effectiveFrom: DateOnly | null;
  effectiveTo: DateOnly | null;
  terms: ContractTerms;
  signerRepresentativeId: number | null;
  contactId: number | null;
  items: ContractItemInput[];
}

export interface ReasonRequest {
  version: RowVersion;
  reasonCode?: string | null;
  reason: string;
}

export type ContractLifecycleAction = 'suspensions' | 'resumptions' | 'completions' | 'terminations' | 'cancellations';

// ---------- Orders ----------
export interface OrderItemInput {
  contractItemId: number;
  qty: Decimal;
}

export interface LocationInput {
  customerAddressId?: number | null;
  addressLine?: string | null;
  provinceCode?: string | null;
  note?: string | null;
}

export interface CreateOrderRequest {
  contractId: number;
  title?: string | null;
  items: OrderItemInput[];
  locations: LocationInput[];
  plannedStart?: DateOnly | null;
  plannedEnd?: DateOnly | null;
  expectedStudents?: number | null;
  specialRequirements?: string | null;
  notes?: string | null;
  /** Optional manual code (create only); null/blank → series ORDER. */
  code?: string | null;
}

export interface UpdateOrderDraftRequest extends Omit<CreateOrderRequest, 'contractId' | 'code'> {
  version: RowVersion;
}

export interface OrderItemDto {
  id: number;
  contractItemId: number;
  contractLineKey: string;
  serviceId: number | null;
  description: string;
  unit: string;
  qtyAllocated: Decimal;
  amountAllocated: Decimal;
}

export interface DeliveryLocationDto {
  id: number;
  sortOrder: number;
  customerAddressId: number | null;
  addressSnapshot: string;
  provinceCode: string | null;
  provinceName: string | null;
  note: string | null;
}

export interface OrderVersionDto {
  id: number;
  versionNo: number;
  state: string;
  contractVersionId: number;
  plannedStart: DateOnly | null;
  plannedEnd: DateOnly | null;
  expectedStudents: number | null;
  specialRequirements: string | null;
  notes: string | null;
  allocatedAmount: Decimal;
  currency: string;
  snapshotHash: string | null;
  submittedAtUtc: UtcDateTime | null;
  approvedAtUtc: UtcDateTime | null;
  basedOnVersionId: number | null;
  changeRequestId: number | null;
  createdAtUtc: UtcDateTime;
  items: OrderItemDto[];
  locations: DeliveryLocationDto[];
}

export interface OrderVersionSummaryDto {
  id: number;
  versionNo: number;
  state: string;
  allocatedAmount: Decimal;
  submittedAtUtc: UtcDateTime | null;
  approvedAtUtc: UtcDateTime | null;
  changeRequestId: number | null;
  createdAtUtc: UtcDateTime;
}

export interface OrderContractRef {
  id: number;
  contractCode: string;
  status: string;
  activeVersionId: number | null;
}

export interface OrderListItem {
  id: number;
  orderCode: string;
  title: string;
  contractId: number;
  contractCode: string;
  customerId: number;
  customerName: string;
  status: string;
  statusName: string;
  salesOwnerUserId: number;
  salesOwnerName: string;
  opsOwnerUserId: number | null;
  opsOwnerName: string | null;
  allocatedAmount: Decimal;
  currency: string;
  plannedStart: DateOnly | null;
  plannedEnd: DateOnly | null;
  createdAtUtc: UtcDateTime;
}

// ---------- Handover (shared with Operations) ----------
export interface ReadinessItem {
  ok: boolean;
  note: string | null;
}

export interface ReadinessChecklist {
  schedule: ReadinessItem | null;
  instructors: ReadinessItem | null;
  students: ReadinessItem | null;
  materials: ReadinessItem | null;
  commercialConditions: ReadinessItem | null;
  specialRequirements: ReadinessItem | null;
}

export type ReadinessKey = keyof ReadinessChecklist;

export const READINESS_ITEMS: { key: ReadinessKey; label: string }[] = [
  { key: 'schedule', label: 'Lịch triển khai' },
  { key: 'instructors', label: 'Giảng viên/nhân sự' },
  { key: 'students', label: 'Học viên' },
  { key: 'materials', label: 'Tài liệu' },
  { key: 'commercialConditions', label: 'Điều kiện thương mại' },
  { key: 'specialRequirements', label: 'Yêu cầu đặc biệt' },
];

export interface HandoverActionDto {
  id: number;
  actionCode: string;
  fromStatus: string;
  toStatus: string;
  actorUserId: number | null;
  actorName: string | null;
  atUtc: UtcDateTime;
  reasonCode: string | null;
  reasonText: string | null;
  payloadJson: string | null;
}

export interface HandoverDto {
  id: number;
  orderId: number;
  orderCode: string;
  orderTitle: string;
  customerName: string;
  status: string;
  statusName: string;
  attemptNo: number;
  ownerUserId: number | null;
  readiness: ReadinessChecklist | null;
  reasonCode: string | null;
  reasonText: string | null;
  missingDocuments: string[];
  assigneeUserId: number | null;
  fixDeadline: DateOnly | null;
  receivedAtUtc: UtcDateTime | null;
  declinedAtUtc: UtcDateTime | null;
  resubmittedAtUtc: UtcDateTime | null;
  resubmitNote: string | null;
  createdAtUtc: UtcDateTime;
  version: RowVersion;
  actions: HandoverActionDto[];
}

export interface AcceptHandoverRequest {
  version: RowVersion;
  readiness: ReadinessChecklist;
  opsOwnerUserId?: number | null;
  note?: string | null;
}

export interface DeclineHandoverRequest {
  version: RowVersion;
  reasonCode: string;
  reasonText: string;
  missingDocuments?: string[] | null;
  assigneeUserId?: number | null;
  fixDeadline?: DateOnly | null;
}

export interface TimelineEntry {
  atUtc: UtcDateTime;
  actionCode: string;
  entityType: string;
  entityId: string;
  actorUserId: number | null;
  actorName: string | null;
  reasonCode: string | null;
  reasonText: string | null;
}

export interface OrderDetail {
  id: number;
  orderCode: string;
  title: string;
  contract: OrderContractRef;
  customerId: number;
  customerName: string;
  status: string;
  statusName: string;
  salesOwnerUserId: number;
  salesOwnerName: string;
  opsOwnerUserId: number | null;
  opsOwnerName: string | null;
  currency: string;
  currentVersionId: number | null;
  approvedVersionId: number | null;
  submittedAtUtc: UtcDateTime | null;
  approvedAtUtc: UtcDateTime | null;
  readyAtUtc: UtcDateTime | null;
  acceptedAtUtc: UtcDateTime | null;
  startedAtUtc: UtcDateTime | null;
  completedAtUtc: UtcDateTime | null;
  closedAtUtc: UtcDateTime | null;
  cancelledAtUtc: UtcDateTime | null;
  statusReasonCode: string | null;
  statusReasonText: string | null;
  version: RowVersion;
  currentVersion: OrderVersionDto | null;
  approvedVersion: OrderVersionDto | null;
  versions: OrderVersionSummaryDto[];
  allocation: LineAllocationDto[];
  handovers: HandoverDto[];
  approval: ApprovalInstanceSummary | null;
  timeline: TimelineEntry[];
  checklist: string[];
  openChangeRequestId: number | null;
  createdAtUtc: UtcDateTime;
}

export interface OrderProgressRequest {
  version: RowVersion;
  note?: string | null;
  readiness?: ReadinessChecklist | null;
}

export type OrderProgressAction = 'starts' | 'holds' | 'partial-completions' | 'completions' | 'closures';

// ---------- Change requests ----------
export type ChangeEntityType = 'CONTRACT' | 'ORDER';

export interface ContractChangeProposal {
  items: ContractItemInput[];
  terms?: ContractTerms | null;
  effectiveFrom?: DateOnly | null;
  effectiveTo?: DateOnly | null;
}

export interface OrderChangeProposal {
  items: OrderItemInput[];
  locations?: LocationInput[] | null;
  plannedStart?: DateOnly | null;
  plannedEnd?: DateOnly | null;
  expectedStudents?: number | null;
  specialRequirements?: string | null;
  notes?: string | null;
}

/** Numbers or strings: impact/proposed JSON is stored without the decimal-string converter. */
export type JsonDecimal = number | string;

export interface ImpactLine {
  key: string;
  description: string;
  change: 'ADDED' | 'REMOVED' | 'MODIFIED' | 'UNCHANGED' | string;
  oldQty: JsonDecimal;
  newQty: JsonDecimal;
  qtyDelta: JsonDecimal;
  oldAmount: JsonDecimal;
  newAmount: JsonDecimal;
  amountDelta: JsonDecimal;
  oldUnitPrice: JsonDecimal | null;
  newUnitPrice: JsonDecimal | null;
}

export interface VersionTotals {
  subtotal: JsonDecimal;
  discountTotal: JsonDecimal;
  netTotal: JsonDecimal;
  taxTotal: JsonDecimal;
  grossTotal: JsonDecimal;
}

export interface ContractImpact {
  currency: string;
  old: VersionTotals;
  new: VersionTotals;
  grossDelta: JsonDecimal;
  netDelta: JsonDecimal;
  taxDelta: JsonDecimal;
  lines: ImpactLine[];
  termsChanged: boolean;
  oldEffectiveFrom: DateOnly | null;
  newEffectiveFrom: DateOnly | null;
  oldEffectiveTo: DateOnly | null;
  newEffectiveTo: DateOnly | null;
  hasChanges: boolean;
}

export interface OrderImpact {
  currency: string;
  oldAllocated: JsonDecimal;
  newAllocated: JsonDecimal;
  amountDelta: JsonDecimal;
  lines: ImpactLine[];
  oldPlannedStart: DateOnly | null;
  newPlannedStart: DateOnly | null;
  oldPlannedEnd: DateOnly | null;
  newPlannedEnd: DateOnly | null;
  oldExpectedStudents: number | null;
  newExpectedStudents: number | null;
  oldLocationCount: number;
  newLocationCount: number;
  locationsChanged: boolean;
  scheduleChanged: boolean;
  hasChanges: boolean;
}

export interface ChangeRequestDto {
  id: number;
  changeRequestCode: string;
  entityType: ChangeEntityType;
  entityId: number;
  entityCode: string | null;
  baseVersionId: number;
  status: string;
  statusName: string;
  reason: string;
  proposed: unknown;
  impact: ContractImpact | OrderImpact | null;
  amountDelta: Decimal;
  currency: string;
  requestedBy: number;
  requestedByName: string | null;
  submittedAtUtc: UtcDateTime | null;
  approvedAtUtc: UtcDateTime | null;
  appliedAtUtc: UtcDateTime | null;
  appliedVersionId: number | null;
  statusReasonText: string | null;
  approval: ApprovalInstanceSummary | null;
  version: RowVersion;
  createdAtUtc: UtcDateTime;
}

export interface CreateChangeRequestRequest {
  entityType: ChangeEntityType;
  entityId: number;
  reason: string;
  contract?: ContractChangeProposal | null;
  order?: OrderChangeProposal | null;
}

export interface UpdateChangeRequestRequest {
  version: RowVersion;
  reason: string;
  contract?: ContractChangeProposal | null;
  order?: OrderChangeProposal | null;
}

// ---------- Approval preview (Approvals module, POST /approvals/requests/preview) ----------
export interface SubmissionPreview {
  requestTypeCode: string;
  subjectType: string;
  subjectId: number;
  subjectCode: string;
  subjectTitle: string;
  amount: Decimal | null;
  currency: string | null;
  checklistErrors: string[];
  attachedDocumentTypes: string[];
  missingDocumentTypes: string[];
  canSubmit: boolean;
  resolution: {
    matched: boolean;
    errorCode: string | null;
    errorMessage: string | null;
    policyCode: string | null;
    policyName: string | null;
    levels: { levelNo: number; name: string; mode: string; minApprovers: number; slaHours: number; approvers: { id: number; displayName: string }[]; problem: string | null }[];
  };
}

// ---------- Quotations (Sales module) — minimal read for "create contract from quotation" ----------
export interface QuotationPickItem {
  id: number;
  quoteCode: string;
  title: string;
  customerId: number;
  customerName: string;
  status: string;
  total: Decimal | null;
  currency: string | null;
  validUntil: DateOnly | null;
}

export interface ListQuery {
  q?: string;
  /** One status or a comma-separated list. */
  status?: string;
  page?: number;
  pageSize?: number;
  /** Date filter (whitelist per endpoint, see docs/web.md → "List filters & deep links"); Vietnam dates, `to` inclusive. */
  dateField?: string;
  from?: string;
  to?: string;
}

const c = '/commercial';

export const commercialApi = {
  // contract types
  contractTypes: (includeInactive = false) => http.get<ContractTypeDto[]>(`${c}/contract-types`, { includeInactive }),
  saveContractType: (id: number | null, req: ContractTypeRequest) =>
    id ? http.put<ContractTypeDto>(`${c}/contract-types/${id}`, req) : http.post<ContractTypeDto>(`${c}/contract-types`, req),

  // contracts
  listContracts: (q: ListQuery & { customerId?: number; ownerId?: number; teamId?: number; contractTypeId?: number }, signal?: AbortSignal) =>
    http.get<PagedResult<ContractListItem>>(`${c}/contracts`, { ...q }, { signal }),
  contract: (id: number) => http.get<ContractDetail>(`${c}/contracts/${id}`),
  contractVersion: (id: number, versionId: number) => http.get<ContractVersionDto>(`${c}/contracts/${id}/versions/${versionId}`),
  contractAllocations: (id: number) => http.get<LineAllocationDto[]>(`${c}/contracts/${id}/allocations`),
  createContract: (req: CreateContractRequest, key: string) => http.post<CreatedResponse>(`${c}/contracts`, req, { idempotencyKey: key }),
  createContractFromQuotation: (req: ContractFromQuotationRequest, key: string) =>
    http.post<CreatedResponse>(`${c}/contracts/from-quotation`, req, { idempotencyKey: key }),
  updateContractDraft: (id: number, req: UpdateContractDraftRequest) => http.put<void>(`${c}/contracts/${id}/draft`, req),
  submitContract: (id: number, note: string | null, key: string) =>
    http.post<ApprovalInstanceRef>(`${c}/contracts/${id}/submissions`, { note }, { idempotencyKey: key }),
  requestSignature: (id: number, version: RowVersion, note: string | null) =>
    http.post<void>(`${c}/contracts/${id}/signature-requests`, { version, note }),
  signContract: (id: number, req: { version: RowVersion; signedAtUtc: UtcDateTime; signedDocumentId: number | null; signerName: string }, key: string) =>
    http.post<void>(`${c}/contracts/${id}/signatures`, req, { idempotencyKey: key }),
  signAmendment: (id: number, amendmentId: number, req: { signedDocumentId: number; signedAtUtc: UtcDateTime }, key: string) =>
    http.post<void>(`${c}/contracts/${id}/amendments/${amendmentId}/signatures`, req, { idempotencyKey: key }),
  contractLifecycle: (id: number, action: ContractLifecycleAction, req: ReasonRequest) => http.post<void>(`${c}/contracts/${id}/${action}`, req),

  // orders
  listOrders: (q: ListQuery & { contractId?: number; customerId?: number; ownerId?: number }, signal?: AbortSignal) =>
    http.get<PagedResult<OrderListItem>>(`${c}/orders`, { ...q }, { signal }),
  order: (id: number) => http.get<OrderDetail>(`${c}/orders/${id}`),
  createOrder: (req: CreateOrderRequest, key: string) => http.post<CreatedResponse>(`${c}/orders/from-contract`, req, { idempotencyKey: key }),
  updateOrderDraft: (id: number, req: UpdateOrderDraftRequest) => http.put<void>(`${c}/orders/${id}/draft`, req),
  submitOrder: (id: number, note: string | null, key: string) =>
    http.post<ApprovalInstanceRef>(`${c}/orders/${id}/submissions`, { note }, { idempotencyKey: key }),
  requestHandoff: (id: number, version: RowVersion) => http.post<void>(`${c}/orders/${id}/handoff-requests`, { version }),
  cancelOrder: (id: number, req: ReasonRequest) => http.post<void>(`${c}/orders/${id}/cancellations`, req),
  orderProgress: (id: number, action: OrderProgressAction, req: OrderProgressRequest) => http.post<void>(`${c}/orders/${id}/${action}`, req),

  // change requests
  listChangeRequests: (q: { entityType?: ChangeEntityType; entityId?: number; status?: string; page?: number; pageSize?: number } & DateRangeParams) =>
    http.get<PagedResult<ChangeRequestDto>>(`${c}/change-requests`, { ...q }),
  changeRequest: (id: number) => http.get<ChangeRequestDto>(`${c}/change-requests/${id}`),
  createChangeRequest: (req: CreateChangeRequestRequest, key: string) =>
    http.post<CreatedResponse>(`${c}/change-requests`, req, { idempotencyKey: key }),
  updateChangeRequest: (id: number, req: UpdateChangeRequestRequest) => http.put<void>(`${c}/change-requests/${id}`, req),
  submitChangeRequest: (id: number, note: string | null, key: string) =>
    http.post<ApprovalInstanceRef>(`${c}/change-requests/${id}/submissions`, { note }, { idempotencyKey: key }),
  applyChangeRequest: (id: number, key: string) => http.post<CreatedResponse>(`${c}/change-requests/${id}/applications`, {}, { idempotencyKey: key }),
  cancelChangeRequest: (id: number, req: { version: RowVersion; reason: string }) => http.post<void>(`${c}/change-requests/${id}/cancellations`, req),

  // approvals preview (Approvals module)
  previewApproval: (requestTypeCode: string, subjectType: string, subjectId: number) =>
    http.post<SubmissionPreview>('/approvals/requests/preview', { requestTypeCode, subjectType, subjectId }),

  // quotations (Sales) — ACCEPTED quotations to convert
  acceptedQuotations: (q: string, signal?: AbortSignal) =>
    http.get<PagedResult<QuotationPickItem>>('/sales/quotations', { status: 'ACCEPTED', q: q || undefined, pageSize: 20 }, { signal }),
};

/** Handover endpoints live under /operations but are implemented by the Commercial module. */
export const handoverApi = {
  queue: (q: { status?: string; ownerId?: number; page?: number; pageSize?: number } & DateRangeParams) => http.get<PagedResult<HandoverDto>>('/operations/handovers', { ...q }),
  reasonCodes: () => http.get<{ code: string; name: string }[]>('/operations/handovers/reason-codes'),
  get: (id: number) => http.get<HandoverDto>(`/operations/handovers/${id}`),
  accept: (id: number, req: AcceptHandoverRequest, key: string) =>
    http.post<void>(`/operations/handovers/${id}/acceptances`, req, { idempotencyKey: key }),
  decline: (id: number, req: DeclineHandoverRequest, key: string) =>
    http.post<void>(`/operations/handovers/${id}/declinations`, req, { idempotencyKey: key }),
  resubmit: (id: number, req: { version: RowVersion; note: string }, key: string) =>
    http.post<void>(`/operations/handovers/${id}/resubmissions`, req, { idempotencyKey: key }),
};

/** Query keys — first segment = domain. */
export const ck = {
  all: ['commercial'] as const,
  contractTypes: (inactive: boolean) => ['commercial', 'contract-types', inactive] as const,
  contracts: ['commercial', 'contracts'] as const,
  contractList: (p: object) => ['commercial', 'contracts', 'list', p] as const,
  contract: (id: number) => ['commercial', 'contracts', 'detail', id] as const,
  contractVersion: (id: number, v: number) => ['commercial', 'contracts', 'detail', id, 'version', v] as const,
  orders: ['commercial', 'orders'] as const,
  orderList: (p: object) => ['commercial', 'orders', 'list', p] as const,
  order: (id: number) => ['commercial', 'orders', 'detail', id] as const,
  changeRequests: ['commercial', 'change-requests'] as const,
  changeRequestList: (p: object) => ['commercial', 'change-requests', 'list', p] as const,
  changeRequest: (id: number) => ['commercial', 'change-requests', 'detail', id] as const,
  handovers: ['operations', 'handovers'] as const,
  handoverQueue: (p: object) => ['operations', 'handovers', 'queue', p] as const,
  handover: (id: number) => ['operations', 'handovers', 'detail', id] as const,
  handoverReasons: ['operations', 'handovers', 'reason-codes'] as const,
};
